import React from 'react'
import type {
  ControllerFieldState,
  ControllerProps,
  ControllerRenderProps
} from 'react-hook-form'
import { Controller } from 'react-hook-form'
import { isNumeric, mergeRefs, useId } from '@mote/helpers'
import { mergeClassnames } from '@mote/helpers/dom'
import styled from '@xstyled/styled-components'
import { forwardRef } from '../../system'
import type { SelectProps } from '../Select'
import { Select } from '../Select'
import type { InputProps } from '../input/input'
import { Input } from '../input/input'
import type { TextAreaProps } from '../textarea'
import { TextArea } from '../textarea'
import type {
  FormFeedbackMessageProps,
  FormGroupProps,
  FormHelpMessageProps,
  FormLabelProps
} from './layout'
import {
  FormFeedbackMessage,
  FormGroup,
  FormHelpMessage,
  FormLabel
} from './layout'

function parseInputValue(value: string | number) {
  return isNumeric(value) ? Number(value) : value
}

export type CustomInputEvent = {
  target: {
    value: string | number
  }
  nativeEvent: React.ChangeEvent<
    HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
  >
}

export interface FormInputProps {
  id?: string
  name: string
  className?: string
  type?: 'input' | 'select' | 'textarea'
  label?: string
  placeholder?: string
  helpText?: string
  required?: boolean
  disabled?: boolean
  parseValue?: boolean
  showRequirementIndicator?: boolean
  rootProps?: FormGroupProps
  labelProps?: FormLabelProps
  inputProps?: InputProps | SelectProps | TextAreaProps
  helpTextProps?: FormHelpMessageProps
  feedbackProps?: FormFeedbackMessageProps
  control: ControllerProps['control']
  shouldUnregister?: ControllerProps['shouldUnregister']
  onChange?: (event: CustomInputEvent) => void
}

export const FormInput = forwardRef<FormInputProps, 'input'>(
  (
    {
      id: idProp,
      name,
      className,
      type = 'input',
      label,
      helpText,
      required = true,
      disabled = false,
      showRequirementIndicator = false,
      rootProps = {},
      inputProps = {},
      labelProps = {},
      helpTextProps = {},
      feedbackProps = {},
      control,
      shouldUnregister = false,
      onChange,
      parseValue = true,
      placeholder
    },
    ref
  ) => {
    const uuid = useId()
    const id = idProp || `field-${uuid}`
    const labelId = `${id}-label`
    const helpId = `${id}-help`
    const feedbackId = `${id}-feedback`

    const { Input, getInputValue } = React.useMemo(() => {
      if (type === 'select')
        return {
          Input: StyledSelect,
          getInputValue: (value: string | number) => ({
            currentValue: value.toString()
          })
        }

      return {
        Input: type === 'textarea' ? StyledTextArea : StyledInput,
        getInputValue: (value: string | number) => ({ value: value.toString() })
      }
    }, [type])

    return (
      <FormGroup
        {...rootProps}
        className={mergeClassnames(
          'form-control',
          className,
          rootProps.className
        )}
        role='group'
      >
        {label && (
          <FormLabel
            required={showRequirementIndicator && required}
            {...labelProps}
            id={labelProps.id ?? labelId}
            htmlFor={labelProps.htmlFor ?? id}
            className={mergeClassnames(
              'form-input__label',
              labelProps.className
            )}
          >
            {label}
          </FormLabel>
        )}
        <Controller
          control={control}
          name={name}
          rules={{ required }}
          shouldUnregister={shouldUnregister}
          render={({
            field: {
              onChange: onFormValueChange,
              value,
              name,
              ref: controlRef
            },
            fieldState: { invalid, error }
          }: {
            field: ControllerRenderProps
            fieldState: ControllerFieldState
          }) => {
            const mergedProps = {
              ...inputProps,
              ...getInputValue(value)
            } as any

            return (
              <>
                <Input
                  {...mergedProps}
                  ref={mergeRefs(controlRef, ref)}
                  id={id}
                  name={name}
                  placeholder={placeholder || mergedProps.placeholder}
                  invalid={invalid}
                  disabled={disabled}
                  className={mergeClassnames(
                    'form-input',
                    inputProps.className
                  )}
                  aria-describedby={
                    invalid && error?.message ? feedbackId : undefined
                  }
                  onChange={(event: CustomInputEvent['nativeEvent']) => {
                    const { value } = event.target
                    const parsedValue = parseValue
                      ? parseInputValue(value)
                      : value

                    const customEvent = {
                      target: {
                        value: parsedValue
                      },
                      nativeEvent: event
                    }

                    onChange?.(customEvent)
                    onFormValueChange(parsedValue)
                  }}
                />
                {helpText && (
                  <FormHelpMessage
                    {...helpTextProps}
                    id={helpId}
                    className={mergeClassnames(
                      'form-input__help-text',
                      helpTextProps.className
                    )}
                  >
                    {helpText}
                  </FormHelpMessage>
                )}
                {invalid && error?.message && (
                  <FormFeedbackMessage
                    {...feedbackProps}
                    id={feedbackId}
                    className={mergeClassnames(
                      'form-input__error-message',
                      feedbackProps.className
                    )}
                    aria-live='polite'
                    isError
                  >
                    {error.message}
                  </FormFeedbackMessage>
                )}
              </>
            )
          }}
        />
      </FormGroup>
    )
  }
)

export const StyledSelect = styled(Select)``

export const StyledInput = styled(Input)``

export const StyledTextArea = styled(TextArea)`
  background: white;
`
